import { Icon } from '@natera/material/lib/icon'
import { Suspense, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useAssignReviewer, useCurrentConsignment } from '~/entities/consignment'
import { usePayors } from '~/entities/payors'
import {
  AuditTrail,
  LoadingSkeleton as AuditTrailLoadingSkeleton,
  useAuditTrail,
} from '~/features/audit-trail'
import { StepSessionProvider } from '~/features/document-analytics'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { formatDateDefault } from '~/shared/lib/datetime'
import { useAppForm } from '~/shared/lib/form'
import { pluralize } from '~/shared/lib/pluralize'
import { Alert, IconButton, LocalIcon, ScrollArea, Text } from '~/shared/ui'
import { useUpdateDocumentPageRanges } from '../api'
import { CONFIDENCE_SCORE_THRESHOLD, FORCE_DISABLE_AUDIT_TRAIL } from '../config'
import {
  getDisplayDocumentId,
  getInitialDocumentPageRanges,
  hasNewDocumentPageRanges,
} from '../lib'
import { DocSplitting } from './doc-splitting/doc-splitting'
import { LoadingSkeleton } from './doc-splitting/loading-skeleton'

export function ConsignmentClusteringPage() {
  const {
    data: { consignment, documents },
  } = useCurrentConsignment()
  const [searchParams] = useSearchParams()
  const documentKey = searchParams.get('navigated_from_id')
  const { assigningInProgress } = useAssignReviewer()
  const { mutateAsync: updateDocumentPageRanges, isError } = useUpdateDocumentPageRanges()
  const [isWarningAlertHiddenManually, setIsWarningAlertHiddenManually] = useState(false)
  const { isAuditTrailVisible: isAuditTrailVisibleDefault } = useAuditTrail()
  const isAuditTrailVisible = FORCE_DISABLE_AUDIT_TRAIL ? false : isAuditTrailVisibleDefault
  const {
    data: {
      data: { insurancePayors: payors },
    },
  } = usePayors()
  const payor = payors.find((p) => p.payorId === consignment.payor_id)

  const defaultValues = useMemo(
    () => ({
      documentPageRanges: getInitialDocumentPageRanges(documents),
    }),
    [documents],
  )

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const body = {
        document_page_ranges: [] as apiClient.DocumentPageRange[],
      }
      if (hasNewDocumentPageRanges(value.documentPageRanges)) {
        body.document_page_ranges = value.documentPageRanges.map((documentPageRange) => ({
          document_id: documentPageRange.documentId,
          page_range: {
            start_page: documentPageRange.pageRange.startPage,
            end_page: documentPageRange.pageRange.endPage,
          },
        }))
      }
      await updateDocumentPageRanges({
        path: {
          key: consignment.key,
        },
        body,
      })
    },
  })

  return (
    <StepSessionProvider
      step="clustering"
      documentKey={documentKey}
    >
      <div className="flex h-full w-full min-w-[1280px] overflow-hidden">
        <ScrollArea className="border-divider bg-surface-low h-full shrink-0 border-r">
          <div className="flex w-[360px] flex-col justify-between">
            <div className="flex flex-col">
              <div className="border-divider border-b px-6 pt-4 pb-2">
                <Text
                  className="pt-px pb-0.5"
                  appearance="H6"
                >
                  Consignment | Clustering
                </Text>
                <div className="mt-3 flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <Text appearance="OVERLINE">Date Received</Text>
                    <Text appearance="BODY2">
                      {consignment.inbox_info?.date
                        ? formatDateDefault(consignment.inbox_info.date)
                        : ''}
                    </Text>
                  </div>
                </div>
              </div>
              <div className="border-divider border-b px-6 py-4">
                <Text appearance="OVERLINE">Consignment Summary</Text>
                <div className="mt-3 flex flex-col gap-3 whitespace-nowrap">
                  <div className="flex items-center justify-between gap-4">
                    <Text
                      className="text-text-secondary"
                      appearance="BODY2"
                    >
                      Lockbox Name
                    </Text>
                    <Text
                      appearance="BODY2"
                      truncated
                      withTooltip
                    >
                      {consignment.inbox_info.name}
                    </Text>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <Text
                      className="text-text-secondary"
                      appearance="BODY2"
                    >
                      Source
                    </Text>
                    <Text
                      appearance="BODY2"
                      truncated
                      withTooltip
                    >
                      {consignment.inbox_info.src}
                    </Text>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <Text
                      className="text-text-secondary"
                      appearance="BODY2"
                    >
                      Lockbox Number
                    </Text>
                    <Text
                      appearance="BODY2"
                      truncated
                      withTooltip
                    >
                      {consignment.inbox_info.cns_number}
                    </Text>
                  </div>
                  {payor && (
                    <div className="flex items-center justify-between gap-4">
                      <Text
                        className="text-text-secondary"
                        appearance="BODY2"
                      >
                        Suggested Payor
                      </Text>
                      <Text
                        appearance="BODY2"
                        truncated
                        withTooltip
                      >
                        {payor.payorName}
                      </Text>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-4">
                    <Text
                      className="text-text-secondary"
                      appearance="BODY2"
                    >
                      Batch Number
                    </Text>
                    <Text
                      appearance="BODY2"
                      truncated
                      withTooltip
                    >
                      {consignment.inbox_info.doc_number ?? 0}
                    </Text>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <Text
                      className="text-text-secondary"
                      appearance="BODY2"
                    >
                      Transaction
                    </Text>
                    <Text
                      appearance="BODY2"
                      truncated
                      withTooltip
                    >
                      {consignment.inbox_info.sequence}
                    </Text>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <Text
                      className="text-text-secondary"
                      appearance="BODY2"
                    >
                      Pages
                    </Text>
                    <Text
                      appearance="BODY2"
                      truncated
                      withTooltip
                    >
                      {consignment.page_range.start_page + 1}-{consignment.page_range.end_page + 1}
                    </Text>
                  </div>
                </div>
              </div>

              <div className="flex flex-col px-6 py-4">
                <Text appearance="OVERLINE">Doc Splitting Details</Text>
                <div className="text-text-secondary mt-3 flex items-center gap-1">
                  <LocalIcon
                    name="opti"
                    size="18"
                  />
                  <Text appearance="SUBTITLE2">Suggested Clustering</Text>
                </div>
                <form.Subscribe
                  selector={(state) => state.values}
                  children={(values) => {
                    const hasLowConfidence = values.documentPageRanges.some(
                      (documentPageRange) =>
                        documentPageRange.confidenceScore < CONFIDENCE_SCORE_THRESHOLD,
                    )
                    return (
                      <>
                        <div className="mt-3 flex flex-col gap-2">
                          {values.documentPageRanges.map((documentPageRange) => {
                            const pageCount =
                              documentPageRange.pageRange.endPage -
                              documentPageRange.pageRange.startPage +
                              1
                            return (
                              <div
                                key={documentPageRange.documentId}
                                className="flex items-center justify-between gap-2"
                              >
                                <div className="flex items-center gap-2">
                                  {documentPageRange.confidenceScore <
                                    CONFIDENCE_SCORE_THRESHOLD && (
                                    <Icon
                                      className="text-alert-warning-message -my-0.5 text-[24px]"
                                      children="help"
                                      fill
                                    />
                                  )}
                                  <Text
                                    className="uppercase"
                                    appearance="BODY2"
                                  >
                                    {documentPageRange.documentId
                                      .toLowerCase()
                                      .startsWith('doc-') ? (
                                      <a
                                        className="text-text-primary"
                                        href={`/documents/${documentPageRange.documentId}`}
                                        target="_blank"
                                      >
                                        {getDisplayDocumentId(documentPageRange.documentId)}
                                      </a>
                                    ) : (
                                      getDisplayDocumentId(documentPageRange.documentId)
                                    )}
                                  </Text>
                                </div>
                                <Text
                                  key={documentPageRange.documentId}
                                  appearance="BODY2"
                                >
                                  {pageCount} {pluralize(pageCount, 'page')}
                                </Text>
                              </div>
                            )
                          })}
                        </div>
                        {hasLowConfidence && !isWarningAlertHiddenManually && (
                          <Alert
                            className="mt-4"
                            appearance="WARNING"
                            materialIcon="warning"
                            action={
                              <IconButton
                                className="text-alert-warning-message"
                                size="medium"
                                container="small"
                                materialIcon="close"
                                onClick={() => setIsWarningAlertHiddenManually(true)}
                              />
                            }
                          >
                            opti wasn’t fully confident about how the pages were grouped. Please
                            review the suggested split before moving forward.
                          </Alert>
                        )}
                      </>
                    )
                  }}
                />
              </div>
            </div>

            <form.Subscribe
              selector={(state) => state.values}
              children={(values) => {
                if (!hasNewDocumentPageRanges(values.documentPageRanges)) {
                  return null
                }
                return (
                  <div className="px-6 pb-2">
                    <Alert
                      appearance="INFO"
                      materialIcon="info"
                    >
                      Once you validate the splits for this consignment, opti will assign an ID to
                      each document created.
                    </Alert>
                  </div>
                )
              }}
            />
          </div>
        </ScrollArea>

        {assigningInProgress ? (
          <>
            <div className="w-full px-8 pt-4">
              <LoadingSkeleton />
            </div>
            {isAuditTrailVisible && <AuditTrailLoadingSkeleton />}
          </>
        ) : (
          <>
            <DocSplitting
              form={form}
              isUpdateError={isError}
            />
            {isAuditTrailVisible && (
              <Suspense fallback={<AuditTrailLoadingSkeleton />}>
                <AuditTrail
                  page="clustering"
                  assetId={consignment.key}
                />
              </Suspense>
            )}
          </>
        )}
      </div>
    </StepSessionProvider>
  )
}
